import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { colors } from '../../styles/colors';

function formatCurrency(value) {
  if (value == null || isNaN(value)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

function RefacTooltip({ daysNormaux, tmpValue, caTmpExcl }) {
  const refacNormale = (daysNormaux || 0) * (tmpValue || 0);
  return (
    <div style={{
      position: 'absolute',
      bottom: '100%',
      right: 0,
      marginBottom: 4,
      backgroundColor: colors.primary.gray,
      color: colors.text.white,
      borderRadius: '0.375rem',
      padding: '0.4rem 0.6rem',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap',
      zIndex: 50,
      boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      lineHeight: 1.5
    }}>
      <div>{daysNormaux || 0} j × {formatCurrency(tmpValue)} = <strong>{formatCurrency(refacNormale)}</strong></div>
      <div>CA exclu TMP = <strong>{formatCurrency(caTmpExcl || 0)}</strong></div>
    </div>
  );
}

/**
 * ConsultantTable - Tableau générique pour afficher données par consultant et par mois
 *
 * @param {Object} props
 * @param {Array} props.monthsData - Données par mois [{month, byConsultant, totalDays, totalCA}]
 * @param {Array} props.consultants - Liste des noms de consultants
 * @param {string} props.valueKey - Clé pour extraire la valeur ('days', 'ca', 'refac')
 * @param {Object} props.totals - Totaux par consultant {consultant: {days, ca, refac}}
 * @param {number} props.totalGlobal - Total global
 * @param {function} props.formatValue - Fonction de formatage des valeurs
 * @param {string} [props.colWidth='auto'] - Largeur des colonnes consultants
 * @param {number} [props.tmpValue] - TMP du trimestre (pour tooltip refac)
 */
function ConsultantTable({
  monthsData,
  consultants,
  valueKey,
  totals,
  totalGlobal,
  formatValue,
  colWidth = 'auto',
  tmpValue
}) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  return (
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.75rem',
      tableLayout: 'fixed'
    }}>
      <colgroup>
        <col style={{ width: '20%' }} />
        {consultants.map(c => (
          <col key={c} style={{ width: colWidth }} />
        ))}
        <col style={{ width: '20%' }} />
      </colgroup>

      <thead>
        <tr style={{ borderBottom: `1px solid ${colors.secondary.lightGray}` }}>
          <th style={{
            padding: '0.25rem 0 0.25rem 0.5rem',
            textAlign: 'left',
            fontWeight: '600',
            color: colors.secondary.mediumGray
          }}>
            Mois
          </th>
          {consultants.map(c => (
            <th
              key={c}
              style={{
                padding: '0.25rem 0',
                textAlign: 'right',
                fontWeight: '600',
                color: colors.secondary.mediumGray
              }}
            >
              {c.split(' ')[0]}
            </th>
          ))}
          <th style={{
            padding: '0.25rem 0.5rem 0.25rem 0',
            textAlign: 'right',
            fontWeight: '700',
            color: colors.primary.teal
          }}>
            Total
          </th>
        </tr>
      </thead>

      <tbody>
        {monthsData.map(md => (
          <tr key={md.month}>
            <td style={{
              padding: '0.2rem 0 0.2rem 0.5rem',
              color: colors.primary.gray
            }}>
              {new Date(0, md.month - 1).toLocaleString('fr-FR', { month: 'short' })}
            </td>
            {consultants.map(c => {
              // Pour 'refac', les données sont dans md.refacByConsultant[c] (nombre)
              // Pour 'days' et 'ca', les données sont dans md.byConsultant[c][valueKey]
              const value = valueKey === 'refac'
                ? md.refacByConsultant?.[c]
                : md.byConsultant?.[c]?.[valueKey];

              const showRefacTooltip = valueKey === 'refac' && tmpValue && value > 0;
              const tooltipKey = `${md.month}-${c}`;
              const consultantData = md.byConsultant?.[c];

              return (
                <td
                  key={c}
                  style={{
                    padding: '0.2rem 0',
                    textAlign: 'right',
                    color: colors.primary.gray,
                    position: 'relative'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    {formatValue(value)}
                    {showRefacTooltip && (
                      <span
                        style={{ cursor: 'pointer', display: 'inline-flex' }}
                        onMouseEnter={() => setActiveTooltip(tooltipKey)}
                        onMouseLeave={() => setActiveTooltip(null)}
                      >
                        <Info size={10} color={colors.primary.teal} />
                        {activeTooltip === tooltipKey && (
                          <RefacTooltip
                            daysNormaux={consultantData?.daysNormaux}
                            tmpValue={tmpValue}
                            caTmpExcl={consultantData?.caTmpExcl}
                          />
                        )}
                      </span>
                    )}
                  </span>
                </td>
              );
            })}
            <td style={{
              padding: '0.2rem 0.5rem 0.2rem 0',
              textAlign: 'right',
              fontWeight: '600',
              color: colors.primary.teal
            }}>
              {formatValue(
                valueKey === 'days' ? md.totalDays :
                valueKey === 'ca' ? md.totalCA :
                valueKey === 'refac' ? md.totalRefac :
                0
              )}
            </td>
          </tr>
        ))}

        {/* Ligne Total */}
        <tr style={{ borderTop: `1px solid ${colors.secondary.mediumGray}` }}>
          <td style={{
            padding: '0.3rem 0 0.3rem 0.5rem',
            fontWeight: '700',
            color: colors.primary.gray
          }}>
            Total
          </td>
          {consultants.map(c => (
            <td
              key={c}
              style={{
                padding: '0.3rem 0',
                textAlign: 'right',
                fontWeight: '700',
                color: colors.primary.gray
              }}
            >
              {formatValue(
                typeof totals[c] === 'object' && totals[c] !== null && valueKey in totals[c]
                  ? totals[c][valueKey]
                  : totals[c]
              )}
            </td>
          ))}
          <td style={{
            padding: '0.3rem 0.5rem 0.3rem 0',
            textAlign: 'right',
            fontWeight: '800',
            color: colors.primary.teal
          }}>
            {formatValue(totalGlobal)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default ConsultantTable;
